import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Sparkles, X, FlaskConical, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { getSamplePreviewEnabled, setSamplePreviewEnabled, subscribeToSamplePreviewMode } from '../lib/samplePreviewMode';
import { areQaFixturesAllowed, QA_FIXTURES_DISABLED_TEST_ID } from '../lib/qaFixturesAvailability';
import { listingAPI, locationAPI, reviewAPI } from '../lib/api';
import { toast } from 'sonner';

const DISMISS_KEY = 'tla_preview_toggle_dismissed';
const FIXTURE_TITLE = '[QA] Admin Review Flow Test Listing';

const QA_PHOTOS = [
  {
    url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80',
    category: 'LIVING_ROOM',
    room_type: 'LIVING_ROOM',
  },
  {
    url: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?auto=format&fit=crop&w=1200&q=80',
    category: 'KITCHEN',
    room_type: 'KITCHEN',
  },
  {
    url: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80',
    category: 'BATHROOM',
    room_type: 'BATHROOM',
  },
  {
    url: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=1200&q=80',
    category: 'FRONT_OF_HOUSE',
    room_type: 'FRONT_OF_HOUSE',
  },
  {
    url: 'https://images.unsplash.com/photo-1615874959474-d609969a20ed?auto=format&fit=crop&w=1200&q=80',
    category: 'BEDROOM',
    room_type: 'BEDROOM',
    bedroom_index: 1,
  },
];

export default function AdminSamplePreviewToggle() {
  const { isAdmin } = useAuth();
  const [enabled, setEnabled] = useState(getSamplePreviewEnabled());
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [staging, setStaging] = useState(false);

  useEffect(() => subscribeToSamplePreviewMode(setEnabled), []);

  if (!isAdmin || dismissed) return null;

  const handleToggle = () => setSamplePreviewEnabled(!enabled);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const ensureQaLocation = async () => {
    const locationsRes = await locationAPI.getAll({ active_only: true, country_code: 'DE' });
    const existing = (locationsRes.data || []).find((loc) =>
      String(loc.name || '').toLowerCase().includes('kaiserslautern')
    );
    if (existing) return existing;

    const created = await locationAPI.create({
      name: 'Kaiserslautern QA Corridor',
      country_code: 'DE',
      region: 'Rheinland-Pfalz',
      lat: 49.4401,
      lng: 7.7491,
      overview: 'QA-only relocation corridor used to stage listing and review moderation flows.',
      lifestyle_notes: 'Close to Ramstein and Kaiserslautern; useful for testing TLA-style housing workflows.',
      nearby_bases: ['Ramstein Air Base', 'Kaiserslautern Military Community'],
      is_active: true,
    });
    return created.data;
  };

  const stageReviewFixture = async () => {
    if (!areQaFixturesAllowed()) {
      toast.error('QA fixtures are disabled in this environment.');
      return;
    }
    setStaging(true);
    try {
      const location = await ensureQaLocation();
      const listingRes = await listingAPI.create({
        title: `${FIXTURE_TITLE} ${new Date().toLocaleTimeString()}`,
        description: 'QA-only listing staged by an admin to validate the owner pending-review state and admin approve/reject workflow.',
        location_id: location.id,
        country_code: 'DE',
        bedrooms: 1,
        bathrooms: 1,
        sqm: 64,
        pet_policy: 'Pets negotiable for QA validation.',
        pets_allowed: true,
        tla_status: 'eligible',
        tla_eligible: true,
        base_currency: 'EUR',
        base_amount: 1450,
        base_rent: 1450,
        utilities_cost: 180,
        electricity_cost: 90,
        internet_cost: 45,
        parking_cost: 60,
        amenities: ['WiFi', 'Parking', 'Washer', 'Balcony', 'Near base'],
        available_from: new Date().toISOString().slice(0, 10),
        min_stay_days: 30,
        property_type: 'Apartment',
        furnishing: 'Furnished',
        kitchen_type: 'Full kitchen',
        has_balcony: true,
        parking_included: false,
        internet_included: true,
        electricity_included: false,
        live_walkthrough_enabled: true,
      });

      const listingId = listingRes.data.id;
      for (const photo of QA_PHOTOS) {
        await listingAPI.addPhoto(listingId, photo);
      }

      await reviewAPI.create({
        listing_id: listingId,
        location_id: location.id,
        rating: 5,
        review_text: 'QA moderation test review. The listing looks clean, near base, and ready for an admin approve or reject validation pass.',
        guided_ratings: {
          cleanliness: 5,
          communication: 5,
          location: 5,
        },
        ai_assisted: false,
        allow_owner_promotion: false,
      });

      toast.success('QA fixture staged. Open Admin > Reviews or Admin > Listings to approve/reject.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stage QA fixture.');
    } finally {
      setStaging(false);
    }
  };

  return (
    <div className="fixed top-24 right-4 z-40 bg-white border border-slate-200 rounded-xl shadow-lg p-4 max-w-xs" data-testid="admin-sample-preview-toggle">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-signal-blue/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-signal-blue" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800">Client preview mode</h3>
          <p className="text-xs text-slate-500 mt-1">Show sample pricing, listings, photos, and reviews on public pages for clean client demos without exposing test login shortcuts.</p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-600 flex-shrink-0 -mt-1 -mr-1"
          title="Dismiss for this session"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <Button onClick={handleToggle} variant={enabled ? 'default' : 'outline'} className={`w-full mt-4 ${enabled ? 'bg-signal-blue hover:bg-signal-blue-hover text-white' : 'border-slate-300 text-slate-700'}`}>
        {enabled ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
        {enabled ? 'Disable sample preview mode' : 'Enable sample preview mode'}
      </Button>
      {areQaFixturesAllowed() ? (
        <Button
          onClick={stageReviewFixture}
          disabled={staging}
          variant="outline"
          className="w-full mt-2 border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="stage-review-fixture-btn"
        >
          {staging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
          Stage review/listing QA fixture
        </Button>
      ) : (
        <p
          className="mt-2 text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50"
          data-testid={QA_FIXTURES_DISABLED_TEST_ID}
        >
          QA fixtures are disabled in this environment.
        </p>
      )}
    </div>
  );
}
